/**
 * Ergonomia tactil: quitar de en medio los gestos del navegador que estorban
 * en un juego de accion.
 *
 * Por que hace falta codigo y no basta el `<meta viewport>`:
 * **iOS Safari ignora `user-scalable=no` desde iOS 10.** El meta sirve en
 * Android y en Chrome, pero en iPad —que es justo donde mas molesta— el zoom
 * por pinza y por doble toque siguen activos salvo que se cancelen a mano.
 *
 * Lo que se bloquea y por que:
 * - **Pinza** (`gesturestart`): un dedo de mas sobre el mando tactil no puede
 *   acabar con el campo ampliado al 200%.
 * - **Doble toque**: pulsar rapido dos veces un boton es lo normal en un juego
 *   (dos pases seguidos); el navegador lo interpreta como "amplia aqui".
 * - **Rebote y tirar-para-recargar** (`overscroll-behavior`, en CSS): arrastrar
 *   hacia abajo durante un partido no puede recargar la pagina.
 *
 * Lo que NO se bloquea: el zoom del sistema operativo (ajustes de
 * accesibilidad). Esto solo desactiva los gestos del navegador dentro del
 * juego, que es donde compiten con los controles.
 */

/** Milisegundos por debajo de los cuales dos toques son "doble toque". */
const DOUBLE_TAP_MS = 320;

export function installTouchErgonomics(target: HTMLElement = document.body): () => void {
  let lastTouchEnd = 0;

  const blockGesture = (event: Event): void => {
    event.preventDefault();
  };

  const onTouchEnd = (event: TouchEvent): void => {
    const now = Date.now();
    // Solo el segundo toque rapido. Cancelar SIEMPRE el touchend rompe el
    // desplazamiento y el foco de los campos de texto.
    if (now - lastTouchEnd < DOUBLE_TAP_MS && event.cancelable) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  };

  // `gesturestart` es propietario de Safari: es la unica forma de cancelar el
  // pinch-zoom ahi. En el resto de navegadores no existe y no pasa nada.
  target.addEventListener("gesturestart", blockGesture);
  target.addEventListener("gesturechange", blockGesture);
  target.addEventListener("gestureend", blockGesture);
  document.addEventListener("touchend", onTouchEnd, { passive: false });

  return () => {
    target.removeEventListener("gesturestart", blockGesture);
    target.removeEventListener("gesturechange", blockGesture);
    target.removeEventListener("gestureend", blockGesture);
    document.removeEventListener("touchend", onTouchEnd);
  };
}
